import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import MatchScoreBadge from 'tio-reimagined/components/match-score-badge';
import SkillChip from 'tio-reimagined/components/skill-chip';
import ActionableCard from 'tio-reimagined/components/actionable-card';
import { eq } from 'tio-ui/utilities';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import {
  PhMagnifyingGlass,
  PhFunnel,
  PhBuildings,
  PhTrendUp,
  PhArrowRight,
  PhBriefcase,
  PhGraduationCap,
  PhCurrencyDollar,
  PhChartLine,
  PhHeart,
  PhUsers,
  PhFlask,
  PhCode,
  PhPaintBrush,
  PhGear,
  PhCalculator,
  PhFirstAid,
  PhBookOpen,
  PhArrowsLeftRight,
} from 'ember-phosphor-icons';
import type { CareerFamily, CareerRole } from 'tio-employee/services/career-pathways-api';

interface CareerPathwaysExploreSignature {
  Args: {
    model: {
      careerFamilies: CareerFamily[];
      featuredRoles: CareerRole[];
    };
  };
}

export default class CareerPathwaysExploreIndex extends Component<CareerPathwaysExploreSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @tracked searchQuery = '';
  @tracked selectedFamily: string | null = null;
  @tracked viewMode: 'grid' | 'list' = 'grid';

  get dataSource(): DataSource {
    // Career explorer data is entirely demo — no backend API yet
    return this.reimaginedData.isDemoMode ? 'demo' : 'placeholder';
  }

  get careerFamilies() {
    return this.args.model.careerFamilies;
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get careerLadderTitle() {
    if (this.isDemoMode) {
      const clientId = getCurrentDemoClientData().client.id;

      if (clientId === 'starbucks') {
        return 'Example Career Ladder: Store Leadership';
      }

      if (clientId === 'ups') {
        return 'Example Career Ladder: Operations Leadership';
      }

      return 'Example Career Ladder: Nursing Leadership';
    }

    const primaryFamily = this.careerFamilies[0]?.name;
    return primaryFamily ? `Example Career Ladder: ${primaryFamily}` : 'Example Career Ladder';
  }

  get careerLadderPreview() {
    if (this.isDemoMode) {
      const clientId = getCurrentDemoClientData().client.id;

      if (clientId === 'starbucks') {
        return [
          { title: 'Barista', level: 'Entry Level', salary: '$30-38k' },
          { title: 'Shift Supervisor', level: 'Mid Level', salary: '$38-48k' },
          { title: 'Store Manager', level: 'Management', salary: '$62-88k' },
          { title: 'District Manager', level: 'Senior Leadership', salary: '$90-120k' },
          { title: 'Regional Director', level: 'Executive', salary: '$120-160k' },
        ];
      }

      if (clientId === 'ups') {
        return [
          { title: 'Package Handler', level: 'Entry Level', salary: '$38-45k' },
          { title: 'Team Lead', level: 'Mid Level', salary: '$45-55k' },
          { title: 'Hub Supervisor', level: 'Management', salary: '$68-92k' },
          { title: 'Operations Manager', level: 'Senior Leadership', salary: '$85-115k' },
          { title: 'Hub Manager', level: 'Executive', salary: '$110-150k' },
        ];
      }

      return [
        { title: 'Staff RN', level: 'Entry Level', salary: '$55-72k' },
        { title: 'Charge Nurse', level: 'Mid Level', salary: '$72-95k' },
        { title: 'Nurse Manager', level: 'Management', salary: '$85-120k' },
        { title: 'Director of Nursing', level: 'Senior Leadership', salary: '$110-145k' },
        { title: 'CNO', level: 'Executive', salary: '$150-250k' },
      ];
    }

    const levelRank: Record<string, number> = {
      Entry: 1,
      Mid: 2,
      Senior: 3,
      Management: 4,
      Executive: 5,
    };

    const ladder = [...this.featuredRoles]
      .sort((a, b) => (levelRank[a.level] || 99) - (levelRank[b.level] || 99))
      .slice(0, 5)
      .map((role) => ({
        title: role.title,
        level: role.level,
        salary: this.formatSalaryRange(role),
      }));

    if (!ladder.length) {
      return [
        { title: 'Associate', level: 'Entry Level', salary: '—' },
        { title: 'Specialist', level: 'Mid Level', salary: '—' },
        { title: 'Senior Specialist', level: 'Senior', salary: '—' },
        { title: 'Manager', level: 'Management', salary: '—' },
        { title: 'Director', level: 'Executive', salary: '—' },
      ];
    }

    return ladder;
  }

  get ladderLevel1() {
    return this.careerLadderPreview[0];
  }

  get ladderLevel2() {
    return this.careerLadderPreview[1];
  }

  get ladderLevel3() {
    return this.careerLadderPreview[2];
  }

  get ladderLevel4() {
    return this.careerLadderPreview[3];
  }

  get ladderLevel5() {
    return this.careerLadderPreview[4];
  }

  get featuredRoles() {
    return this.args.model.featuredRoles;
  }

  @cached
  get selectedFamilyName() {
    if (!this.selectedFamily) return '';
    return this.careerFamilies.find((f) => f.id === this.selectedFamily)?.name || '';
  }

  @cached
  get filteredRoles() {
    let roles = this.featuredRoles;

    if (this.selectedFamily) {
      const familyName = this.careerFamilies.find((f) => f.id === this.selectedFamily)?.name;
      roles = roles.filter((r) => r.family === familyName);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      roles = roles.filter(
        (r) =>
          r.title.toLowerCase().includes(query) ||
          this.getRoleSkillNames(r).some((s) => s.toLowerCase().includes(query))
      );
    }

    return roles;
  }

  setFamily = (familyId: string | null) => {
    this.selectedFamily = this.selectedFamily === familyId ? null : familyId;
  };

  updateSearch = (event: Event) => {
    this.searchQuery = (event.target as HTMLInputElement).value;
  };

  getRoleSkillNames = (role: CareerRole) => {
    const required = role.requiredSkills.map((skill) => skill.name);
    const preferred = role.preferredSkills.map((skill) => skill.name);
    return [...required, ...preferred].slice(0, 4);
  };

  formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  formatSalaryRange = (role: CareerRole) => {
    return `${this.formatCurrency(role.salaryMin)} - ${this.formatCurrency(role.salaryMax)}`;
  };

  formatGrowthRate = (role: CareerRole) => {
    return `+${role.growthRate}%`;
  };

  getFamilyIcon = (iconName: string) => {
    const icons: Record<string, unknown> = {
      code: PhCode,
      'first-aid': PhFirstAid,
      calculator: PhCalculator,
      'paint-brush': PhPaintBrush,
      flask: PhFlask,
      'book-open': PhBookOpen,
    };
    return icons[iconName] || PhBriefcase;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <DataIndicator @source={{this.dataSource}} @showBadge={{true}} @dataSourceId="career-plan">
      <VStack class="data-audit w-full min-w-0">
        {{! Search and Filter Bar }}
        <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div class="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div class="flex-1 relative">
              <PhMagnifyingGlass
                @size={{20}}
                class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search roles, skills, or keywords..."
                value={{this.searchQuery}}
                {{on "input" this.updateSearch}}
                class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>
            <div class="flex gap-2">
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhFunnel}}
                @label="Filters"
              />
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhBuildings}}
                @label="Company Openings Only"
              />
              <LinkTo
                @route="authenticated.reimagined.explore.compare"
                class="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-lg font-medium hover:bg-violet-200 transition-colors"
              >
                <PhArrowsLeftRight @size={{18}} />
                Compare Careers
              </LinkTo>
            </div>
          </div>
        </div>

        {{! Career Families }}
        <Section class="bg-white rounded-lg shadow-sm mb-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhBriefcase @size={{20}} class="text-violet-600" />
              <span>Browse by Career Family</span>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {{#each this.careerFamilies as |family|}}
                <button
                  type="button"
                  {{on "click" (fn this.setFamily family.id)}}
                  class="p-4 rounded-lg border-2 transition-all text-center hover:shadow-md
                    {{if
                      (eq this.selectedFamily family.id)
                      'border-violet-500 bg-violet-50'
                      'border-gray-200 hover:border-violet-300'
                    }}"
                >
                  <div
                    class="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center
                      {{if
                        (eq this.selectedFamily family.id)
                        'bg-violet-500 text-white'
                        'bg-gray-100 text-gray-600'
                      }}"
                  >
                    {{#let (this.getFamilyIcon family.icon) as |Icon|}}
                      {{#if Icon}}
                        {{! @glint-ignore - dynamic icon component }}
                        <Icon @size={{24}} />
                      {{/if}}
                    {{/let}}
                  </div>
                  <div class="font-medium text-gray-900 text-sm">{{family.name}}</div>
                  <div class="text-xs text-gray-500">{{family.rolesCount}} roles</div>
                  <div class="text-xs text-green-600 font-medium mt-1">{{family.growth}}
                    growth</div>
                </button>
              {{/each}}
            </div>
          </:body>
        </Section>

        {{! Featured/Filtered Roles }}
        <Section class="bg-white rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <PhTrendUp @size={{20}} class="text-violet-600" />
                <span>
                  {{#if this.selectedFamily}}
                    {{this.filteredRoles.length}}
                    Roles in
                    {{this.selectedFamilyName}}
                  {{else}}
                    Featured Career Paths
                  {{/if}}
                </span>
              </div>
              <Text @intent="muted" @size="sm">Sorted by match score</Text>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {{#each this.filteredRoles as |role|}}
                <ActionableCard
                  @cardClass="group bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:border-violet-300 hover:shadow-lg transition-all cursor-pointer"
                >
                  <:header>
                    {{! Header with match score }}
                    <div class="flex justify-between items-start mb-3">
                      <div class="flex-1 min-w-0 pr-3">
                        <Heading
                          @level={{4}}
                          class="text-lg font-semibold text-gray-900 group-hover:text-violet-600"
                        >
                          {{role.title}}
                        </Heading>
                        <Text @intent="muted" @size="sm">{{role.family}} • {{role.level}}</Text>
                      </div>
                      <MatchScoreBadge @score={{role.matchScore}} @label="Match" @size="md" />
                    </div>

                    {{! Badges }}
                    <div class="flex gap-2 mb-3">
                      {{#if role.isHighDemand}}
                        <Chip @intent="warning">
                          <span class="inline-flex items-center">
                            <PhTrendUp @size={{14}} class="mr-1" />
                            High Demand
                          </span>
                        </Chip>
                      {{/if}}
                      {{#if role.companyOpenings}}
                        <Chip @intent="success">
                          <span class="inline-flex items-center">
                            <PhBuildings @size={{14}} class="mr-1" />
                            {{role.companyOpenings}}
                            at Your Company
                          </span>
                        </Chip>
                      {{/if}}
                    </div>

                    {{! Salary }}
                    <div class="flex items-center gap-2 mb-3">
                      <PhCurrencyDollar @size={{18}} class="text-green-600" />
                      <Text class="font-medium text-gray-900">{{this.formatSalaryRange role}}</Text>
                      <Text @intent="muted" @size="sm">
                        ({{this.formatGrowthRate role}}
                        10yr growth)
                      </Text>
                    </div>
                  </:header>

                  <:skills>
                    {{#each (this.getRoleSkillNames role) as |skill|}}
                      <SkillChip @name={{skill}} @intent="default" @showLevel={{false}} />
                    {{/each}}
                  </:skills>

                  <:actions>
                    <Button @intent="primary" @appearance="outlined" @size="sm">
                      View Career Path
                    </Button>
                    <PhArrowRight
                      @size={{18}}
                      class="text-violet-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </:actions>
                </ActionableCard>
              {{/each}}
            </div>

            {{#if (eq this.filteredRoles.length 0)}}
              <div class="text-center py-12">
                <PhMagnifyingGlass @size={{48}} class="mx-auto text-gray-300 mb-4" />
                <Heading @level={{3}} class="text-gray-600 mb-2">No roles found</Heading>
                <Text @intent="muted">Try adjusting your search or filters</Text>
              </div>
            {{/if}}
          </:body>
        </Section>

        {{! Career Ladder Preview }}
        <Section class="bg-white rounded-lg shadow-sm mt-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhChartLine @size={{20}} class="text-violet-600" />
              <span>{{this.careerLadderTitle}}</span>
            </div>
          </:header>
          <:body>
            <div class="relative py-8">
              {{! Connecting line - positioned to go through icon centers }}
              <div
                class="absolute top-[52px] left-[10%] right-[10%] h-1 bg-gradient-to-r from-violet-200 via-violet-400 to-violet-500 rounded-full"
              ></div>

              <div class="relative flex justify-between items-start px-4">
                {{! Level 1 }}
                <div class="flex flex-col items-center w-[18%]">
                  <div
                    class="w-14 h-14 rounded-full bg-violet-100 border-4 border-violet-200 flex items-center justify-center relative z-10"
                  >
                    <PhGraduationCap @size={{22}} class="text-violet-600" />
                  </div>
                  <div class="mt-3 text-center">
                    <div
                      class="font-semibold text-gray-900 text-sm"
                    >{{this.ladderLevel1.title}}</div>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel1.level}}</Text>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel1.salary}}</Text>
                  </div>
                </div>

                {{! Level 2 }}
                <div class="flex flex-col items-center w-[18%]">
                  <div
                    class="w-14 h-14 rounded-full bg-violet-200 border-4 border-violet-300 flex items-center justify-center relative z-10"
                  >
                    <PhChartLine @size={{22}} class="text-violet-600" />
                  </div>
                  <div class="mt-3 text-center">
                    <div
                      class="font-semibold text-gray-900 text-sm"
                    >{{this.ladderLevel2.title}}</div>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel2.level}}</Text>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel2.salary}}</Text>
                  </div>
                </div>

                {{! Level 3 - Current (highlighted) }}
                <div class="flex flex-col items-center w-[18%]">
                  <div
                    class="w-16 h-16 rounded-full bg-violet-500 border-4 border-violet-600 flex items-center justify-center relative z-10 shadow-lg -mt-1"
                  >
                    <PhUsers @size={{24}} class="text-white" />
                  </div>
                  <div class="mt-3 text-center">
                    <div class="font-bold text-violet-700 text-sm">{{this.ladderLevel3.title}}</div>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel3.level}}</Text>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel3.salary}}</Text>
                    <Chip @intent="primary" class="mt-1">Your Goal</Chip>
                  </div>
                </div>

                {{! Level 4 }}
                <div class="flex flex-col items-center w-[18%]">
                  <div
                    class="w-14 h-14 rounded-full bg-violet-300 border-4 border-violet-400 flex items-center justify-center relative z-10"
                  >
                    <PhGear @size={{22}} class="text-violet-700" />
                  </div>
                  <div class="mt-3 text-center">
                    <div
                      class="font-semibold text-gray-900 text-sm"
                    >{{this.ladderLevel4.title}}</div>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel4.level}}</Text>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel4.salary}}</Text>
                  </div>
                </div>

                {{! Level 5 }}
                <div class="flex flex-col items-center w-[18%]">
                  <div
                    class="w-14 h-14 rounded-full bg-violet-400 border-4 border-violet-500 flex items-center justify-center relative z-10"
                  >
                    <PhHeart @size={{22}} class="text-white" />
                  </div>
                  <div class="mt-3 text-center">
                    <div
                      class="font-semibold text-gray-900 text-sm"
                    >{{this.ladderLevel5.title}}</div>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel5.level}}</Text>
                    <Text @intent="muted" @size="xs">{{this.ladderLevel5.salary}}</Text>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 text-center">
              <Button
                @intent="primary"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="Explore Full Career Ladders"
              />
            </div>
          </:body>
        </Section>
      </VStack>
    </DataIndicator>
  </template>
}
